import CryptoJS from 'crypto-js';
import { Config } from 'shared/lib/config';
import { ANALYTIC_TYPE } from 'shared/constants/analytics';
import {
  encryptMenus,
  encryptCompany,
  encryptClaimTabSettings,
} from 'shared/lib/tokenEncryption';
// Direct module imports (not the `shared/lib/permissions` barrel): the barrel
// pulls in PermissionProvider, which imports this file (import cycle).
import { MENU_REGISTRY } from 'shared/lib/permissions/menu-registry';
import { resolveAccess } from 'shared/lib/permissions/resolve-access';

const isValidJSON = (str, clear = false) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    if (clear) {
      clearLocalStorageData();
      window.location.href = '/login';
    } else return false;
  }
};

const parseIfJSON = (str, clear = false) => {
  if (!str) return null;

  if (isValidJSON(str, clear)) {
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      return str;
    }
  }

  return str;
};

// Safe AES decrypt — CryptoJS throws "Malformed UTF-8 data" when the stored
// ciphertext doesn't decrypt cleanly (stale value, rotated/mismatched key,
// corruption). Returning null instead of throwing lets callers fail closed
// (treated as logged-out / no data) rather than crashing the render tree.
const _safeDecrypt = (cipherText, key, label) => {
  try {
    return CryptoJS.AES.decrypt(cipherText, key).toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error(`[authUtils] Failed to decrypt ${label}:`, error);
    return null;
  }
};

export const checkTokenAuth = () => {
  const keyToken = localStorage.getItem('token');

  if (keyToken) {
    return _safeDecrypt(keyToken, Config.REACT_APP_ENCRYPT_KEY_TOKEN, 'token');
  }
};

export const checkRefreshTokenAuth = () => {
  const keyRefreshToken = localStorage.getItem('refresh_token');

  if (keyRefreshToken) {
    return _safeDecrypt(
      keyRefreshToken,
      Config.REACT_APP_ENCRYPT_KEY_REFRESH_TOKEN,
      'refresh_token'
    );
  }
};

export const checkEmail = () => {
  const keyEmail = localStorage.getItem('email');

  if (keyEmail) {
    return _safeDecrypt(keyEmail, Config.REACT_APP_ENCRYPT_KEY_EMAIL, 'email');
  }
};

export const checkRole = () => {
  const keyRole = localStorage.getItem('role');

  if (keyRole) {
    const decryptedString = _safeDecrypt(
      keyRole,
      Config.REACT_APP_ENCRYPT_KEY_ROLE,
      'role'
    );

    if (!decryptedString) return null;
    const decryptedRole = parseIfJSON(decryptedString);

    if (typeof decryptedRole === 'object') {
      return decryptedRole?.role;
    } else {
      return decryptedRole;
    }
  }
};

export const checkUser = () => {
  const keyUser = localStorage.getItem('user');

  if (keyUser) {
    const decryptedString = _safeDecrypt(
      keyUser,
      Config.REACT_APP_ENCRYPT_KEY_USER,
      'user'
    );
    if (!decryptedString) return null;

    const decryptedUser = parseIfJSON(decryptedString);

    return decryptedUser;
  }
};

// Reader for company access stored from GET /me — shape:
// { company_id, company_features, tamper_setting, ticket_number_setting,
//   analytic_setting }.
export const checkCompany = () => {
  const keyCompany = localStorage.getItem('company');
  if (!keyCompany) return null;

  const decryptedString = _safeDecrypt(
    keyCompany,
    Config.REACT_APP_ENCRYPT_KEY_COMPANY ?? Config.REACT_APP_ENCRYPT_KEY_MENUS,
    'company'
  );

  if (!decryptedString) return null;
  return parseIfJSON(decryptedString);
};

// Reader for claim tab/section settings stored from GET /me — shape:
// [{ id, claim_tab_id, name, alias, display_order, is_visible, section_settings }].
export const checkClaimTabSettings = () => {
  const stored = localStorage.getItem('claim_tab_settings');

  if (!stored) return null;
  const decrypted = _safeDecrypt(
    stored,
    Config.REACT_APP_ENCRYPT_KEY_CLAIM_TAB_SETTINGS ??
      Config.REACT_APP_ENCRYPT_KEY_MENUS,
    'claim_tab_settings'
  );
  if (!decrypted) return null;
  return parseIfJSON(decrypted);
};

// Payment method -> company_feature.type from GET /me `company_features`.
// Type names are historical: INPATIENT_CASHLESS represents the Cashless feature,
// OUTPATIENT_REIMBURSEMENT represents the Reimbursement feature (each gates BOTH
// admission types for its payment method). See SA-1514.
export const CLAIM_FEATURE_TYPE = {
  CASHLESS: 'INPATIENT_CASHLESS',
  REIMBURSEMENT: 'OUTPATIENT_REIMBURSEMENT',
  QUALITY_CHECKER: 'QC_CLAIM',
};

// Derive claim payment-method access from company_features.
// Defaults to the persisted company access (checkCompany); a method is active only
// when its feature entry has is_active === true — absent/false => inactive (hidden).
export const getClaimFeatureAccess = (companyFeatures) => {
  const features = companyFeatures ?? checkCompany()?.company_features ?? [];
  const isActive = (type) =>
    features.some((f) => f?.type === type && f?.is_active === true);

  const isCashlessActive = isActive(CLAIM_FEATURE_TYPE.CASHLESS);
  const isReimbursementActive = isActive(CLAIM_FEATURE_TYPE.REIMBURSEMENT);
  // Quality Checker gate (SA-1552): active only when a QC_CLAIM feature entry
  // exists AND is_active === true. Absent entry or is_active false => hidden.
  const isQualityCheckerActive = isActive(CLAIM_FEATURE_TYPE.QUALITY_CHECKER);

  return {
    isCashlessActive,
    isReimbursementActive,
    isQualityCheckerActive,
    isAnyClaimFeatureActive: isCashlessActive || isReimbursementActive,
  };
};

// Tamper detection (SA-1789) is a company-level setting of its own, not a claim
// payment-method feature — kept out of getClaimFeatureAccess so the two gates
// stay independent. A null/absent setting row means never enabled => off.
// No checkCompany() fallback here (unlike getClaimFeatureAccess): the only
// consumer is the reactive hook, which always supplies the projected value.
export const getTamperDetectionAccess = (tamperSetting) => ({
  isTamperDetectionActive: tamperSetting?.is_enabled === true,
});

// Unique ticket-number detection ("Deteksi Unik Nomor Tiket", SA-1851). Same
// shape as the tamper setting: nullable, so an absent row means never enabled.
export const getTicketNumberDetectionAccess = (ticketNumberSetting) => ({
  isTicketNumberDetectionActive: ticketNumberSetting?.is_enabled === true,
});

// Analytics dashboard format (SA-1849). Company-level like tamper_setting, not a
// claim payment-method feature. The match is POSITIVE on the exact ZAI_DASHBOARD
// literal — never `!== DEFAULT` — so an absent row, a null setting, or an unknown
// future enum value all resolve to DEFAULT rather than flipping every company
// onto the simplified card. Same no-fallback contract as
// getTamperDetectionAccess: the only consumer is the reactive hook.
export const getAnalyticFormat = (analyticSetting) => {
  const analyticType =
    analyticSetting?.analytic_type === ANALYTIC_TYPE.ZAI_DASHBOARD
      ? ANALYTIC_TYPE.ZAI_DASHBOARD
      : ANALYTIC_TYPE.DEFAULT;

  return {
    analyticType,
    isSimplifiedAnalytics: analyticType === ANALYTIC_TYPE.ZAI_DASHBOARD,
  };
};

const ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN'];

const _decryptRolePayload = () => {
  const keyRole = localStorage.getItem('role');
  if (!keyRole) return null;

  const decryptedString = _safeDecrypt(
    keyRole,
    Config.REACT_APP_ENCRYPT_KEY_ROLE,
    'role'
  );

  if (!decryptedString) return null;
  return parseIfJSON(decryptedString, true);
};

const _normalizeRoleString = (role) =>
  typeof role === 'string' ? role.toUpperCase() : '';

export const checkIsAdministrator = () => {
  const decryptedRole = _decryptRolePayload();
  if (decryptedRole === null) return false;

  if (typeof decryptedRole === 'object') {
    return (
      ADMIN_ROLES.includes(_normalizeRoleString(decryptedRole?.role)) ||
      Boolean(decryptedRole?.not_editable)
    );
  }
  return ADMIN_ROLES.includes(_normalizeRoleString(decryptedRole));
};

export const checkIsSuperAdmin = () => {
  const decryptedRole = _decryptRolePayload();
  if (decryptedRole === null) return false;

  const roleString =
    typeof decryptedRole === 'object' ? decryptedRole?.role : decryptedRole;
  return _normalizeRoleString(roleString) === 'SUPER_ADMIN';
};

export const checkIsRegularAdmin = () => {
  const decryptedRole = _decryptRolePayload();
  if (decryptedRole === null) return false;

  const roleString =
    typeof decryptedRole === 'object' ? decryptedRole?.role : decryptedRole;
  return _normalizeRoleString(roleString) === 'ADMIN';
};

let _menusCache = null;
let _menusCacheFingerprint = null;

// SUPER-ADMIN STATIC MENUS — menu entries + permissions hardcoded on the FE
// for super-admin (role === 'SUPER_ADMIN') because the BE has not yet
// provisioned menu data or per-route access for these routes. Regular ADMIN
// users and users flagged `not_editable` must NOT see these menus.
// TODO(SA-1501): remove static injection once BE rolls out the menu + role
// permissions for /management-company; the route will then resolve through
// the normal BE-driven menu list.
const SUPER_ADMIN_STATIC_MENUS = [
  {
    id: 99001,
    name: 'Company',
    frontend_endpoint: '/management-company',
    can_create: true,
    can_read: true,
    can_update: true,
    can_delete: true,
  },
];

const _getDecryptedMenusRaw = () => {
  const encrypted = localStorage.getItem('menus');
  if (!encrypted) {
    _menusCache = null;
    _menusCacheFingerprint = null;
    return null;
  }
  if (_menusCacheFingerprint === encrypted && _menusCache !== null) {
    return _menusCache;
  }
  try {
    const decryptedString = CryptoJS.AES.decrypt(
      encrypted,
      Config.REACT_APP_ENCRYPT_KEY_MENUS
    ).toString(CryptoJS.enc.Utf8);
    const decryptedMenus = JSON.parse(decryptedString);
    _menusCache = decryptedMenus;
    _menusCacheFingerprint = encrypted;
    return decryptedMenus;
  } catch (error) {
    console.error('[authUtils] Failed to decrypt or parse menus:', error);
    _menusCache = null;
    _menusCacheFingerprint = null;
    return null;
  }
};

const _getDecryptedMenus = () => {
  const base = _getDecryptedMenusRaw();
  // Unauthenticated state — return null so consumers fail closed.
  if (base === null) return null;
  // Inject super-admin static menus only when the current user is a
  // super-admin (role === 'SUPER_ADMIN'). Regular admins and other roles
  // see only the BE-provisioned menu list.
  if (checkIsSuperAdmin()) {
    return [...(Array.isArray(base) ? base : []), ...SUPER_ADMIN_STATIC_MENUS];
  }
  return base;
};

export const _invalidateMenusCache = () => {
  _menusCache = null;
  _menusCacheFingerprint = null;
};

export const checkMenus = () => _getDecryptedMenus();

export const findMenuAccess = (pathname = '/') => {
  const menus = _getDecryptedMenus();
  if (!Array.isArray(menus)) return null;
  for (const menu of menus) {
    if (menu?.sub_menus) {
      const found = menu.sub_menus.find(
        (sub) => sub?.frontend_endpoint === pathname
      );
      if (found) return found;
    } else if (menu?.frontend_endpoint === pathname) {
      return menu;
    }
  }
  return null;
};

// Build the stored menus array from GET /me data.
// Relocated from usePostLoginSetup.filterMenusByRole and re-sourced from
// /me.menus (a superset carrying is_shown + can_* + sub_menus), so a single
// source reproduces the previous login + /menus filtering behavior.
const SUPER_ADMIN_MENU_SLUGS = [
  'company-log-buku-tarif',
  'company-log-insurance-claim',
];

const _buildMenusFromMe = (meData) => {
  const menus = meData?.menus ?? [];

  if (meData?.role?.toLowerCase() === 'super_admin') {
    return menus.filter((item) => SUPER_ADMIN_MENU_SLUGS.includes(item?.slug));
  }

  const shownMenuIds = new Set();
  const collectShownId = (item) => {
    const isVisible =
      item?.is_shown &&
      item?.frontend_endpoint !== '' &&
      !SUPER_ADMIN_MENU_SLUGS.includes(item?.slug);
    if (isVisible) shownMenuIds.add(item.id);
  };

  menus.forEach((menu) => {
    const hasSubMenus = Array.isArray(menu?.sub_menus);
    if (menu?.is_shown && !hasSubMenus) {
      collectShownId(menu);
      return;
    }
    if (hasSubMenus) {
      menu.sub_menus.forEach((sub) => {
        if (sub?.is_shown) {
          collectShownId(sub);
          collectShownId(menu);
        }
      });
    }
  });

  return menus
    .filter((menu) => shownMenuIds.has(menu?.id))
    .map((menu) => {
      if (menu?.sub_menus?.length > 0) {
        return {
          ...menu,
          sub_menus: menu.sub_menus.filter((sub) => shownMenuIds.has(sub?.id)),
        };
      }
      return menu;
    });
};

// Pure writer — persists menus + company access from a GET /me data object.
// Does NOT write the `role` key, so the login-captured `not_editable` flag
// (absent from /me) is preserved across refreshes.
export const applyPermissionsFromMe = (meData) => {
  if (!meData) return;

  const menusArray = _buildMenusFromMe(meData);
  localStorage.setItem('menus', encryptMenus(menusArray));
  localStorage.setItem(
    'company',
    encryptCompany({
      company_id: meData?.company_id ?? null,
      company_features: meData?.company_features ?? [],
      // Rides inside the existing company blob rather than a 4th storage key: it
      // is company-scoped like company_features, so PermissionProvider's
      // WATCHED_STORAGE_KEYS already covers it (SA-1789).
      tamper_setting: meData?.tamper_setting ?? null,
      // Same reasoning as tamper_setting — company-scoped, so it rides the
      // existing watched `company` key instead of a new one (SA-1851).
      ticket_number_setting: meData?.ticket_number_setting ?? null,
      // Same rationale as tamper_setting — company-scoped, so it rides the same
      // blob and needs no new watched storage key (SA-1849).
      analytic_setting: meData?.analytic_setting ?? null,
      // Claim-detail layout toggle (SA-2092) — company-scoped, same blob, no new
      // watched storage key.
      claim_detail_setting: meData?.claim_detail_setting ?? null,
    })
  );
  localStorage.setItem(
    'claim_tab_settings',
    encryptClaimTabSettings(meData?.claim_tab_settings ?? [])
  );
  _invalidateMenusCache();
  // Same-tab provider re-sync: the browser `storage` event only fires in other
  // tabs, so dispatch a custom event for PermissionProvider in this tab.
  window.dispatchEvent(new Event('permissions:updated'));
};

// Longest-prefix match of pathname against menu frontend_endpoints, so detail
// routes (e.g. /insurance-portal/123) resolve to their parent menu and do not
// trigger a false "access revoked". Distinct from findMenuAccess (exact match),
// which existing consumers rely on — do NOT change findMenuAccess.
// Sub-flow routes that have no menu entry of their own because they are opened
// from a parent menu's page (e.g. /policy-management/:companyId is reached from
// the company detail page). Resolve their access against the owning menu so the
// access-revoked check does not treat them as forbidden and bounce the user to
// menus[0]. Mirrors the active-parent mapping in Sidebar.jsx.
const ROUTE_ACCESS_ALIASES = [
  { prefix: '/policy-management', resolveAs: '/management-company' },
  // SA-1862 — Konfigurasi Klaim is a super-admin sub-flow reached from the
  // company detail page and has no BE menu of its own, so it resolves its
  // access against Company Management. Without this the access-revoke check
  // below bounces the super admin out of the area on every token refresh.
  { prefix: '/claim-configuration', resolveAs: '/management-company' },
  // No QC Dashboard Analytics entry (SA-2118): `/qc-analytics-dashboard` has
  // its own BE menu (slug `company-insurance-claim-qc-analytic`) and resolves
  // by that slug (then by endpoint) below — it must never borrow Analytics
  // access.
];

// MENU_REGISTRY routes that declare a BE menu `slug`, longest endpoint first,
// so a nested registry route wins over its parent.
const SLUG_REGISTRY_ROUTES = Object.keys(MENU_REGISTRY)
  .filter((key) => MENU_REGISTRY[key]?.slug && MENU_REGISTRY[key]?.endpoint)
  .map((key) => ({ key, ...MENU_REGISTRY[key] }))
  .sort((a, b) => b.endpoint.length - a.endpoint.length);

// Same slug-first resolution as `resolveAccess` / the sidebar for registry
// routes: the menu is found by its stable BE slug even when the BE changes
// its `frontend_endpoint`. Returns null when the route has no slug entry or
// no menu carries the slug (the caller then matches by endpoint).
const resolveBySlug = (menus, pathname) => {
  const route = SLUG_REGISTRY_ROUTES.find(
    (entry) =>
      pathname === entry.endpoint || pathname.startsWith(`${entry.endpoint}/`)
  );
  if (!route) return null;

  const access = resolveAccess(menus, route.key);
  // `accessFallback` (e.g. QC Dashboard Analytics → Quality Checker) is
  // honoured here too, so a token refresh never revokes what the router allowed.
  const fallbackSlug = MENU_REGISTRY[route.accessFallback]?.slug;
  const isRouteSlug = access?.slug === route.slug;
  const isFallbackSlug = Boolean(fallbackSlug) && access?.slug === fallbackSlug;
  return isRouteSlug || isFallbackSlug ? access : null;
};

// SA-1552 — Quality Checker is injected into the sidebar from the FE for
// ADMIN and USER; BE menu data (the /me response that backs the stored `menus`)
// does not include it yet. Because it has no BE menu entry, the access-revoke
// check below would treat it as forbidden after a token refresh re-pulls /me
// and rewrites `menus`, hard-redirecting the user to menus[0] (Insurance
// Portal List) on tab refocus. Exempt FE-static routes from the revoke check
// so users are not bounced off a page they are allowed to use. Mirrors
// STATIC_FE_MENUS in templates/sidebar/Sidebar.jsx.
// TODO(SA-1552): remove once BE provisions /quality-checker in /me menus.
const ACCESS_REVOKE_EXEMPT_PREFIXES = ['/quality-checker'];

export const resolveActiveMenuAccess = (pathname = '/') => {
  const menus = _getDecryptedMenus();
  if (!Array.isArray(menus)) return null;

  const alias = ROUTE_ACCESS_ALIASES.find(
    (entry) =>
      pathname === entry.prefix || pathname.startsWith(`${entry.prefix}/`)
  );
  const effectivePathname = alias ? alias.resolveAs : pathname;

  const bySlug = resolveBySlug(menus, effectivePathname);
  if (bySlug) return bySlug;

  const candidates = [];
  menus.forEach((menu) => {
    if (Array.isArray(menu?.sub_menus) && menu.sub_menus.length > 0) {
      menu.sub_menus.forEach((sub) => candidates.push(sub));
    } else {
      candidates.push(menu);
    }
  });

  let best = null;
  let bestLength = -1;
  candidates.forEach((candidate) => {
    const endpoint = candidate?.frontend_endpoint;
    if (!endpoint) return;
    const isPrefix =
      effectivePathname === endpoint ||
      effectivePathname.startsWith(`${endpoint}/`);
    if (isPrefix && endpoint.length > bestLength) {
      best = candidate;
      bestLength = endpoint.length;
    }
  });

  return best;
};

// Redirect to the first allowed menu when the user has lost access to the
// route they are currently on. Single source of truth for the revoke check —
// consumed by both refreshToken.js and setupHttpClient.js. Fails open when
// menus is empty (avoids redirect loops during transient empty states).
export const redirectIfAccessRevoked = (menus) => {
  if (!Array.isArray(menus) || menus.length === 0) return;

  const { pathname } = window.location;
  const isExemptRoute = ACCESS_REVOKE_EXEMPT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  if (isExemptRoute) return;

  const access = resolveActiveMenuAccess(pathname);
  const stillAllowed = Boolean(access) && access?.can_read !== false;
  if (stillAllowed) return;

  const first = menus[0];
  const target =
    first?.sub_menus?.[0]?.frontend_endpoint ?? first?.frontend_endpoint ?? '/';
  window.location.href = target;
};

export const clearLocalStorageData = () => {
  const authKeys = [
    'token',
    'refresh_token',
    'email',
    'role',
    'user',
    'menus',
    'company',
    'claim_tab_settings',
    'isWebView',
    'sharedToken',
  ];
  authKeys.forEach((key) => localStorage.removeItem(key));
  _invalidateMenusCache();
};
